import { useCallback } from 'react';
import { useToast } from '@/lib/ui';
import { isAppError } from '@/lib/api/errors';

/**
 * Maps a thrown AppError to the right toast treatment (plan §4.1 / §5.1). Validation errors
 * are handled inline by forms, so this skips them by default. Always surfaces request_id.
 */
type ApiErrorOpts = { onValidation?: (fields: Record<string, string>) => void };

export function useApiError() {
  const toast = useToast();
  // Rest params keep this assignable to react-query's onError(error, variables, context)
  // for any mutation variable type, while still accepting our optional opts object.
  return useCallback(
    (error: unknown, ...rest: unknown[]) => {
      const opts = rest[0] as ApiErrorOpts | undefined;
      if (!isAppError(error)) {
        toast.error('Something went wrong.', {
          description: error instanceof Error ? error.message : undefined,
        });
        return;
      }
      switch (error.code) {
        case 'validation_error':
          opts?.onValidation?.(error.fieldErrors());
          if (!opts?.onValidation)
            toast.error('Please fix the highlighted fields.', { requestId: error.requestId });
          break;
        case 'forbidden':
          toast.warning("You don't have permission to do that.");
          break;
        case 'conflict':
          toast.error(error.message || 'That identifier is already in use.', {
            requestId: error.requestId,
          });
          break;
        case 'rate_limited':
          toast.warning('Too many requests. Please wait a moment and try again.');
          break;
        case 'not_found':
          toast.error('That item no longer exists.');
          break;
        default:
          toast.error(error.message || 'Something went wrong.', { requestId: error.requestId });
      }
    },
    [toast],
  );
}
